import { validateHoursConfig } from './schedule';

/**
 * ELNKWEB-3246: the call center's hours of operation (Cxone), in Eastern.
 *
 * Ported from e-commerce-checkout (src/callCenter/hours.js) so .net and .com switch between the
 * call and buy flows at the same moments. A change here belongs in both repos.
 *
 * This supersedes REACT_APP_CALL_CENTER_TIME, which could express only one window per day, at
 * whole-hour granularity, with no way to state a holiday or a closure. That variable is still
 * wired through .env -> bitbucket-pipelines.yml -> k8s/service-env.tmpl.yaml -> src/config.js
 * but is no longer read. See the deprecation note in src/config.js.
 *
 * MAINTAINING THIS FILE
 *
 * Hours live in code, so a change here needs a deploy. That is a deliberate trade for now and
 * is expected to move to the checkout service later; resolveCallCenterOpen() already takes the
 * config as an argument, so that migration will not touch any call site.
 *
 * Each weekday holds a LIST of open intervals. One representation covers every case:
 *
 *   [{ start: '09:00', end: '22:00' }]                              open 9am-10pm
 *   [{ start: '09:00', end: '12:00' }, { start: '13:00', end: '17:00' }]   split shift
 *   []                                                              closed all day
 *
 * Times are 24-hour 'HH:mm' and comparison is half-open (start <= t < end), so an end of
 * '23:00' means open through 22:59 and closed at 23:00 exactly.
 */

const WEEKDAY_HOURS = [{ start: '07:30', end: '23:00' }];
const WEEKEND_HOURS = [{ start: '09:00', end: '22:00' }];

export const CALL_CENTER_HOURS = {
  // One call center, one zone. Sites do not get their own timezone - the schedule describes
  // when agents are at their desks, which is an Eastern fact regardless of where the visitor is.
  timezone: 'America/New_York',

  schedule: {
    mon: WEEKDAY_HOURS,
    tue: WEEKDAY_HOURS,
    wed: WEEKDAY_HOURS,
    thu: WEEKDAY_HOURS,
    fri: WEEKDAY_HOURS,
    sat: WEEKEND_HOURS,
    sun: WEEKEND_HOURS,
  },

  /**
   * Per-date exceptions, keyed 'YYYY-MM-DD' in Eastern. A date listed here replaces that day's
   * weekday hours outright.
   *
   *   '2026-11-26': []                                   closed all day - buy flow only
   *   '2026-12-24': [{ start: '09:00', end: '17:00' }]   reduced hours
   *
   * Add each year's holidays before the year starts. An unlisted date simply uses its weekday.
   */
  dateOverrides: {
    '2026-09-07': [{ start: '09:00', end: '19:00' }], // Labor Day - reduced hours
  },
};

// A malformed schedule must never reach a build. Outside production this throws at module load,
// so `npm test` and the dev server fail loudly and immediately. In production the same config is
// re-checked defensively by resolveCallCenterOpen, which degrades to "closed" - routing the
// customer to the buy flow, where they can still transact - rather than throwing inside a render.
if (process.env.NODE_ENV !== 'production') {
  const errors = validateHoursConfig(CALL_CENTER_HOURS);

  if (errors.length) {
    throw new Error(`Invalid CALL_CENTER_HOURS config:\n  - ${errors.join('\n  - ')}`);
  }
}
